import { useEffect, useId, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { PencilIcon, XIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type AnimalRow,
  firstFailureMessage,
  useEditAnimals,
  WRITABLE_FIELDS,
} from '@/services/animal-management';
import {
  OPTION_SET_OBJECT_TYPES,
  type OptionSetAttribute,
} from '@/services/animal-management/api';
import { useToast } from '@/services/toast';

import { LookupCombobox, type LookupKind } from './LookupCombobox';
import { OptionSetSelect } from './OptionSetSelect';
import { buildBulkEditInputs } from './write-builders';

type BulkEditAnimalsDialogProps = {
  open: boolean;
  selectedAnimals: AnimalRow[];
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

function useSchema() {
  const { t } = useTranslation();
  return useMemo(
    () =>
      z.object({
        ownerId: z.string().nullable(),
        yardId: z.string().nullable(),
        penId: z.string().nullable(),
        status: z.string(),
        weight: z.preprocess(
          (val) => (val === '' || val === undefined ? undefined : val),
          z.coerce
            .number({
              invalid_type_error: t(
                'animalManagement.bulkEdit.invalidNumber',
                'Enter a valid number',
              ),
            })
            .int(
              t('animalManagement.writes.wholeNumber', 'Enter a whole number'),
            )
            .min(
              0,
              t('animalManagement.writes.wholeNumber', 'Enter a whole number'),
            )
            .optional(),
        ),
        opuProgram: z.string(),
        birthDate: z.date().optional(),
      }),
    [t],
  );
}

export type BulkEditFormValues = z.infer<ReturnType<typeof useSchema>>;

type FieldName = keyof BulkEditFormValues;

const DONOR_TYPES = new Set(['Donor', 'LOPU Donor']);

type BulkEditFieldProps = {
  control: ReturnType<typeof useForm<BulkEditFormValues>>['control'];
  name: FieldName;
  label: string;
  placeholder?: string;
  type?: 'text' | 'number' | 'select' | 'date';
  lookup?: LookupKind;
  optionSet?: { objectTypeCode: number; attribute: OptionSetAttribute };
  options?: Array<{
    label: string;
    value: string;
  }>;
};

function BulkEditField({
  control,
  label,
  name,
  options,
  placeholder,
  type,
  lookup,
  optionSet,
}: BulkEditFieldProps) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <FieldLabel htmlFor={id}>{label}</FieldLabel>

          {lookup ? (
            <LookupCombobox
              id={id}
              kind={lookup}
              value={(field.value as string | null) ?? null}
              onChange={field.onChange}
              placeholder={label}
              invalid={fieldState.invalid}
            />
          ) : optionSet ? (
            <OptionSetSelect
              id={id}
              objectTypeCode={optionSet.objectTypeCode}
              attribute={optionSet.attribute}
              value={field.value as string}
              onChange={field.onChange}
              invalid={fieldState.invalid}
            />
          ) : type === 'select' ? (
            <Select
              value={(field.value as string) ?? ''}
              onChange={field.onChange}
            >
              <SelectTrigger id={id}>
                <SelectValue
                  placeholder={t('common.selectOption', 'Select Option')}
                />
              </SelectTrigger>
              <SelectContent>
                {options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : type === 'date' ? (
            <DatePicker
              value={field.value as Date | undefined}
              onChange={field.onChange}
              className="h-8"
              id={id}
            />
          ) : (
            <Input
              {...field}
              value={(field.value as string) ?? ''}
              id={id}
              type={type ?? 'text'}
              placeholder={placeholder}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}

export function BulkEditAnimalsDialog({
  open,
  selectedAnimals,
  onOpenChange,
  onSuccess,
}: BulkEditAnimalsDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const formId = useId();
  const schema = useSchema();
  const editAnimals = useEditAnimals();
  const form = useForm<BulkEditFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      ownerId: null,
      yardId: null,
      penId: null,
      status: '',
      weight: undefined,
      opuProgram: '',
      birthDate: undefined,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset();
    }
  }, [form, open, selectedAnimals]);

  const donorAnimals = useMemo(
    () =>
      selectedAnimals.filter((animal) => DONOR_TYPES.has(animal.animalType)),
    [selectedAnimals],
  );
  const calfAnimals = useMemo(
    () => selectedAnimals.filter((animal) => animal.animalType === 'Calf'),
    [selectedAnimals],
  );

  const weightAnimalCount = useMemo(
    () =>
      selectedAnimals.filter((animal) =>
        WRITABLE_FIELDS[animal.animalType].has('weight'),
      ).length,
    [selectedAnimals],
  );

  const watchedValues = form.watch() as BulkEditFormValues;
  const hasUpdates = useMemo(
    () =>
      Boolean(
        watchedValues.ownerId ||
          watchedValues.yardId ||
          watchedValues.penId ||
          watchedValues.status.trim() ||
          typeof watchedValues.weight === 'number' ||
          watchedValues.opuProgram.trim() ||
          watchedValues.birthDate,
      ),
    [watchedValues],
  );

  const onSubmit = async (values: BulkEditFormValues) => {
    const { inputs, skipped } = buildBulkEditInputs(selectedAnimals, values);

    const results = inputs.length ? await editAnimals.mutateAsync(inputs) : [];
    const succeeded = results.filter((r) => r.success).length;
    const failedCount = results.length - succeeded;

    if (failedCount === 0) {
      toast.show({
        severity: 'success',
        summary: t('common.success', 'Success'),
        detail: [
          t('animalManagement.writes.animalsUpdated', {
            defaultValue: '{count} animal(s) updated.',
            count: succeeded,
          }),
          skipped.length
            ? t('animalManagement.writes.skipped', {
                defaultValue:
                  '{count} skipped: none of the chosen fields apply to them.',
                count: skipped.length,
              })
            : '',
        ]
          .filter(Boolean)
          .join(' '),
      });
    } else {
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t('animalManagement.writes.partialUpdate', {
          defaultValue: '{succeeded} of {total} animals updated. {message}',
          succeeded,
          total: results.length,
          message: firstFailureMessage(results) ?? '',
        }),
        life: 10000,
      });
    }

    if (succeeded > 0) onSuccess();
    if (failedCount === 0) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t('animalManagement.bulkEdit.title', {
              defaultValue: 'Bulk Edit Animals ({count} selected)',
              count: selectedAnimals.length,
            })}
          </DialogTitle>
          <DialogDescription>
            {t(
              'animalManagement.bulkEdit.description',
              'Select which fields to update and enter the values to apply to all selected animals.',
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody
          as="form"
          id={formId}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-5"
        >
          <Card className="border-blue-200 bg-blue-50/70">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-slate-900">
                {t('animalManagement.bulkEdit.selectedAnimals', {
                  defaultValue: 'Selected Animals ({count})',
                  count: selectedAnimals.length,
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>
                      {t('animalManagement.columns.nameTag', 'Name (Tag)')}
                    </TableHead>
                    <TableHead>
                      {t('animalManagement.columns.animalType', 'Animal Type')}
                    </TableHead>
                    <TableHead>
                      {t('animalManagement.columns.ranch', 'Ranch')}
                    </TableHead>
                    <TableHead>
                      {t('animalManagement.columns.status', 'Status')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedAnimals.map((animal) => (
                    <TableRow key={animal.id}>
                      <TableCell>{animal.nameTag}</TableCell>
                      <TableCell>{animal.animalType}</TableCell>
                      <TableCell>{animal.ranch}</TableCell>
                      <TableCell>
                        {animal.stateCode === 1
                          ? t('common.inactive', 'Inactive')
                          : t('common.active', 'Active')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-slate-900">
                {t(
                  'animalManagement.bulkEdit.commonFields',
                  'Common Fields (All Animals)',
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <BulkEditField
                control={form.control}
                name="ownerId"
                lookup="owner"
                label={t('animalManagement.columns.ranch', 'Ranch')}
              />
              <BulkEditField
                control={form.control}
                name="yardId"
                lookup="yard"
                label={t('animalManagement.columns.yard', 'Yard')}
              />
              <BulkEditField
                control={form.control}
                name="status"
                label={t('animalManagement.columns.status', 'Status')}
                type="select"
                options={[
                  {
                    label: t('common.active', 'Active'),
                    value: 'active',
                  },
                  {
                    label: t('common.inactive', 'Inactive'),
                    value: 'inactive',
                  },
                ]}
              />
              {weightAnimalCount > 0 && (
                <BulkEditField
                  control={form.control}
                  name="weight"
                  type="number"
                  placeholder="0"
                  label={t('animalManagement.bulkEdit.weightLabel', {
                    defaultValue:
                      'Weight (lbs) - Recipient/Calf only, applies to {count}',
                    count: weightAnimalCount,
                  })}
                />
              )}
            </CardContent>
          </Card>

          {donorAnimals.length > 0 && (
            <Card className="border-fuchsia-200 bg-fuchsia-50/50">
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-fuchsia-900">
                  {t('animalManagement.bulkEdit.donorFields', {
                    defaultValue:
                      'Donor/LOPU Donor Fields (Applies to {count} donor animals)',
                    count: donorAnimals.length,
                  })}
                </CardTitle>
                <CardDescription className="text-xs text-fuchsia-700">
                  {t(
                    'animalManagement.bulkEdit.donorFieldsDescription',
                    'These fields only update Donor and LOPU Donor animals.',
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <BulkEditField
                  control={form.control}
                  name="opuProgram"
                  label={t(
                    'animalManagement.columns.opuProgram',
                    'OPU Program',
                  )}
                  optionSet={{
                    objectTypeCode: OPTION_SET_OBJECT_TYPES.donor,
                    attribute: 'tog_opuprogram',
                  }}
                />
              </CardContent>
            </Card>
          )}

          {calfAnimals.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/60">
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-amber-900">
                  {t('animalManagement.bulkEdit.calfFields', {
                    defaultValue:
                      'Calf Fields (Applies to {count} calf animals)',
                    count: calfAnimals.length,
                  })}
                </CardTitle>
                <CardDescription className="text-xs text-amber-700">
                  {t(
                    'animalManagement.bulkEdit.calfFieldsDescription',
                    'These fields only update Calf animals.',
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <BulkEditField
                  control={form.control}
                  name="birthDate"
                  type="date"
                  label={t('animalManagement.columns.birthDate', 'Birth Date')}
                />
                <BulkEditField
                  control={form.control}
                  name="penId"
                  lookup="pen"
                  label={t('animalManagement.columns.housing', 'Housing')}
                />
              </CardContent>
            </Card>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <XIcon className="mr-2 size-4" />
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={!hasUpdates || editAnimals.isPending}
          >
            <PencilIcon className="mr-2 size-4" />
            {t('animalManagement.bulkEdit.updateAnimals', {
              defaultValue: 'Update {count} Animals',
              count: selectedAnimals.length,
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
